import { TeamMemberRole } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { $account } from '@/models/account.ts'
import { listQueryKey } from '@/modules/tasks/query/task-list-query.ts'
import { useTaskService } from '@/modules/tasks/service/task-fetch-context'
import type { Task, TaskForm } from '@/modules/tasks/service/task-service'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

export const taskQueryKey = 'tasks'

export function useTasks(options?: { enabled: boolean }) {
  const { list } = useTaskService()
  const account = useUnit($account)

  return useQuery(
    taskQueryKey,
    async () => {
      const tasks = await list()

      if (account?.memberRole !== TeamMemberRole.MEMBER) {
        return tasks
      }

      return tasks.filter((task) =>
        task.assignees?.some((assignee) => assignee.id === account.memberId),
      )
    },
    options,
  )
}

export function useCreateTask() {
  const client = useQueryClient()
  const { create } = useTaskService()

  return useMutation(create, {
    onMutate: async (task: TaskForm) => {
      const current = client.getQueryData<Task[]>(taskQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = [...validEntries, task]

        client.setQueryData(taskQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(taskQueryKey),
  })
}

export function useUpdateTaskList() {
  const client = useQueryClient()
  const { updateTaskList } = useTaskService()

  return useMutation(updateTaskList, {
    onSettled: () => {
      client.invalidateQueries(taskQueryKey)
      client.invalidateQueries(listQueryKey)
      client.invalidateQueries('task-list')
    },
  })
}

export function useDeleteTask() {
  const client = useQueryClient()
  const { delete: delete_ } = useTaskService()

  return useMutation(delete_, {
    onMutate: async (id: number) => {
      const current = client.getQueryData<Task[]>(taskQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.filter((entry) => entry.id !== id)

        client.setQueryData(taskQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(taskQueryKey),
  })
}

interface UseUpdateTaskSettings {
  onlyInvalidateOnError: boolean
}
const defaultUpdateTaskSettings: UseUpdateTaskSettings = {
  onlyInvalidateOnError: false,
}

export function useUpdateTask(settings = defaultUpdateTaskSettings) {
  const { onlyInvalidateOnError } = settings
  const client = useQueryClient()
  const { update } = useTaskService()

  return useMutation(update, {
    onMutate: async ({ id, ...form }) => {
      const current = client.getQueryData<Task[]>(taskQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.map((entry) =>
          entry.id === id ? { ...entry, ...form } : entry,
        )

        client.setQueryData(taskQueryKey, updated)
      }

      return current
    },
    onError: () => {
      if (onlyInvalidateOnError) {
        return client.invalidateQueries(taskQueryKey)
      }
    },
    onSettled: onlyInvalidateOnError
      ? undefined
      : () => client.invalidateQueries(taskQueryKey),
  })
}
